import Vue from 'vue'
import uploadCenterMixin from '@/mixins/uploadCenterMixin'
import {
  isCloudUploadAbort,
  MAX_CLOUD_UPLOAD_FILE_SIZE,
  uploadCloudResourceFile
} from '@/utils/cloudUpload'
import { getActiveCloudServerKey } from '@/utils/cloudServer'
import { getAppUiText } from '@/utils/language/common'

const MAX_CLOUD_UPLOAD_BATCH_COUNT = 50
const CLOUD_UPLOAD_BATCH_LIMIT_MESSAGE = '单次上传最多支持 50 个文件，请分批上传。'

function getFileExtension(fileName) {
  const value = String(fileName || '').trim().toLowerCase()
  const dotIndex = value.lastIndexOf('.')
  return dotIndex >= 0 && dotIndex < value.length - 1 ? value.slice(dotIndex + 1) : ''
}

function createUploadTaskId() {
  return `upload-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function createAbortController() {
  if (typeof AbortController == 'undefined') {
    return null
  }
  return new AbortController()
}

export default {
  mixins: [uploadCenterMixin],
  computed: {
    cloudUploadTemplateEnabled() {
      return this.$options.cloudUploadTemplateImport === true
    },
    cloudUploadCurrentFolder() {
      const folders = Array.isArray(this.paperFolderPath) ? this.paperFolderPath : []
      return folders.length > 0 ? folders[folders.length - 1] : null
    },
    cloudUploadIsTemplateFolder() {
      const folder = this.cloudUploadCurrentFolder
      if (!folder || !this.cloudUploadTemplateEnabled) {
        return false
      }
      const resourceType = Number(folder.resourceType)
      return (Number.isFinite(resourceType) && resourceType === 5) || folder.template === true
    },
    cloudUploadAcceptExtensions() {
      const extensions = this.cloudUploadIsTemplateFolder
        ? (this.$options.cloudUploadTemplateExtensions || ['png', 'jpg', 'jpeg'])
        : (this.$options.cloudUploadExtensions || [])
      return Array.isArray(extensions) ? extensions : []
    },
    cloudUploadAccept() {
      return this.cloudUploadAcceptExtensions.map(item => `.${item}`).join(',')
    },
    cloudUploadFolderType() {
      return this.$options.cloudUploadFolderType || this.folderType || ''
    },
    cloudUploadAppType() {
      return Number(this.$options.cloudUploadAppType || this.appType || 0)
    },
    cloudUploadButtonText() {
      const ui = this.getCloudUploadUi()
      if (this.cloudUploadIsTemplateFolder) {
        return ui.importTemplate || 'Import Template'
      }
      if (this.cloudUploadFolderType == 'learning') {
        return ui.importBooks || ui.importData || 'Import Books'
      }
      return ui.importPdf || 'Import PDF'
    }
  },
  methods: {
    getCloudUploadUi() {
      return this.ui || getAppUiText(this.language)
    },
    openCloudUploadDialog() {
      const input = this.$refs.cloudUploadInput
      if (input && typeof input.click == 'function') {
        input.click()
      }
    },
    handleCloudUploadChange(event) {
      const input = event && event.target ? event.target : null
      const files = input && input.files ? Array.from(input.files) : []
      if (input) {
        input.value = ''
      }
      this.enqueueCloudUploadFiles(files)
    },
    enqueueCloudUploadFiles(files) {
      const list = Array.isArray(files) ? files : []
      if (list.length > MAX_CLOUD_UPLOAD_BATCH_COUNT) {
        this.$message.error(CLOUD_UPLOAD_BATCH_LIMIT_MESSAGE)
        return false
      }
      list.forEach(file => {
        if (!this.isCloudUploadFileAllowed(file)) {
          return
        }
        const task = this.createCloudUploadTask(file)
        this.$enqueuePaperUploadTask(task, this.runCloudUploadTask)
      })
      return true
    },
    isCloudUploadFileAllowed(file) {
      const ui = this.getCloudUploadUi()
      if (!file) {
        return false
      }
      if (Number(file.size) > MAX_CLOUD_UPLOAD_FILE_SIZE) {
        this.$message.error(ui.uploadFileSizeLimit)
        return false
      }
      const extension = getFileExtension(file.name)
      if (!this.cloudUploadAcceptExtensions.includes(extension)) {
        this.$message.error(ui.uploadTypeNotSupported)
        return false
      }
      return true
    },
    createCloudUploadTask(file) {
      const ui = this.getCloudUploadUi()
      return {
        id: createUploadTaskId(),
        file,
        fileName: file.name,
        fileSize: file.size || 0,
        folderType: this.cloudUploadFolderType,
        appType: this.cloudUploadAppType,
        parentId: this.paperListResourceId || '',
        serverKey: getActiveCloudServerKey(),
        abortController: null,
        progress: 0,
        state: 'running',
        statusText: ui.uploading,
        cancelled: false
      }
    },
    runCloudUploadTask(task) {
      if (!task || !task.file || task.cancelled) {
        return
      }
      const ui = this.getCloudUploadUi()
      const abortController = createAbortController()
      Vue.set(task, 'abortController', abortController)
      Vue.set(task, 'state', 'running')
      Vue.set(task, 'statusText', ui.uploading)
      Vue.set(task, 'progress', 0)

      uploadCloudResourceFile({
        file: task.file,
        fileName: task.fileName,
        folderType: task.folderType,
        appType: task.appType,
        parentId: task.parentId,
        serverKey: task.serverKey,
        signal: abortController ? abortController.signal : undefined,
        onProgress: progress => {
          const total = Number(progress && progress.total) || task.fileSize || 0
          if (!total) {
            return
          }
          const loaded = Math.max(0, Number(progress.loaded) || 0)
          Vue.set(task, 'progress', Math.max(0, Math.min(99, Math.floor((loaded / total) * 100))))
        }
      }).then(() => {
        if (task.cancelled) {
          return
        }
        Vue.set(task, 'progress', 100)
        Vue.set(task, 'state', 'done')
        Vue.set(task, 'statusText', ui.uploadDone)
        this.refreshAfterCloudUpload(task)
      }).catch(error => {
        if (task.cancelled || isCloudUploadAbort(error)) {
          return
        }
        Vue.set(task, 'state', 'error')
        Vue.set(task, 'statusText', error && error.message ? error.message : ui.uploadFailed)
      })
    },
    refreshAfterCloudUpload() {
      if (typeof this.loadPaperList == 'function') {
        this.loadPaperList({ silent: true, preserveScroll: true })
      }
    }
  }
}
